/*
 * Copyright (c) 2023 Class Technologies Inc. All rights reserved.
 */
import { useMemo } from 'react';

import CopyIcon from '@mui/icons-material/ContentCopy';
import { IconButton, Link, Table, TableCell, TableHead, TableRow } from '@mui/material';
import dateFormat from 'date-fns-tz/format';
import { type TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import Loadable from '../common/loadable';

type LegacyRecordingsTableProps = {
    recordings: any; // Not going to bother creating types for legacy stuff that will be replaced soon.
    isLoading: boolean;
};

type RecordingFile = {
    url: string;
    label: string;
};

type Recording = {
    uuid: string;
    dateTimeStr: string;
    recordingName: string;
    streamLink?: string;
    password: string;
    recordingFiles: RecordingFile[];
};

// Create recording object for each meeting returned from zoom.
const buildRecordings = (data, t: TFunction): Recording[] =>
    !data?.meetings
        ? []
        : data.meetings.map((m) => {
              let startTime = new Date(m.start_time);
              let endTime;
              if (m?.recording_files && m.recording_files.length > 0) {
                  const [firstRecording] = m.recording_files;
                  startTime = new Date(firstRecording.recording_start);
                  // Sometimes recording_end is not set because the recording is still processing.
                  endTime = firstRecording.recording_end ? new Date(firstRecording.recording_end) : null;
              }
              const startDateStr = dateFormat(new Date(startTime), 'PPP');
              const endDateStr = endTime ? dateFormat(new Date(endTime), 'PPP') : '';
              const startTimeStr = dateFormat(new Date(startTime), 'p');
              const endTimeStr = endTime ? dateFormat(new Date(endTime), 'p') : '';
              let dateTimeStr;
              if (startDateStr === endDateStr) {
                  // End of recording did not cross into following day/s.
                  // Format date time string in shorter format.
                  dateTimeStr = `${startDateStr} ${startTimeStr} - ${endTimeStr}`;
              } else {
                  // End of recording went beyond start date.
                  // Format date time string in longer format.
                  dateTimeStr = `${startDateStr} ${startTimeStr} - ${endDateStr} ${endTimeStr}`;
              }

              const recordingTime = dateFormat(new Date(startTime), 'PP/LL p');
              const recordingName = `${m.topic} ${t('class:recording')} - ${recordingTime}`;

              // Only include the password if there is NOT a passcode we can glue on to the end of the url.
              const password = m.recording_play_passcode ? null : m.password || null;
              // Try create a password link to be glued onto the end of the streamLink.
              const pwdLink = m.recording_play_passcode ? `?pwd=${m.recording_play_passcode}` : '';
              const streamLink = m.share_url ? `${m.share_url}${pwdLink}` : undefined;

              // Add links to download individual recording files:
              const recordingFiles: RecordingFile[] = [];
              m.recording_files?.forEach((r) => {
                  const url = r.download_url;
                  if (url) {
                      const label = t(`class:recording-type-${r.recording_type}`);
                      recordingFiles.push({ url, label });
                  }
              });

              const recording: Recording = {
                  uuid: m.uuid,
                  dateTimeStr,
                  recordingName,
                  streamLink,
                  password,
                  recordingFiles
              };

              return recording;
          });

type LegacyStreamLinkProps = {
    recording: Recording;
    t: TFunction;
};
const LegacyStreamLink: React.FC<LegacyStreamLinkProps> = ({ recording, t }) => (
    <div>
        <div>
            <a target="_blank" href={recording.streamLink}>
                {recording.recordingName}
            </a>
        </div>
        {recording.password && (
            <div>
                <span>
                    {t('class:password')}: {recording.password}{' '}
                </span>
                <IconButton
                    aria-label={t('lti:recordings.copy-pwd-to-clipboard')}
                    onClick={() => navigator.clipboard.writeText(recording.password)}
                >
                    <CopyIcon />
                </IconButton>
            </div>
        )}
    </div>
);

const LegacyRecordingsTable: React.FC<LegacyRecordingsTableProps> = ({ recordings, isLoading }) => {
    const { t } = useTranslation('class');

    const recordingsData = useMemo(() => buildRecordings(recordings, t), [recordings, t]);

    return (
        <Loadable fullScreen={false} isLoading={isLoading}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>{t('time')}</TableCell>
                        <TableCell>{t('recording')}</TableCell>
                    </TableRow>
                    {recordingsData.map((recording) => (
                        <TableRow key={recording.uuid}>
                            <TableCell>{recording.dateTimeStr}</TableCell>
                            <TableCell>
                                {recording.streamLink ? (
                                    // If we have a streamLink then show that without individual files.
                                    <LegacyStreamLink {...{ recording, t }} />
                                ) : (
                                    // If we don't have a streamLink but we do have recordingFiles then list them.
                                    recording.recordingFiles &&
                                    recording.recordingFiles.map((file) => (
                                        <Link
                                            sx={{ display: 'block' }}
                                            key={file.url}
                                            href="{file.url}"
                                            underline="hover"
                                        >
                                            {file.label}
                                        </Link>
                                    ))
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableHead>
            </Table>
        </Loadable>
    );
};

export default LegacyRecordingsTable;
